
import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosConfig';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import '../styles.css';

const UserDashboard = () => {
  const [userData, setUserData] = useState(null);
  const [punchStatus, setPunchStatus] = useState(false); 
  const [attendanceData, setAttendanceData] = useState(null);
  const [branch, setBranch] = useState(null);
  const currentUserId = localStorage.getItem('id');
  const [roles, setRoles] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [userQueries, setUserQueries] = useState([]);
  const [isUserPopupVisible, setIsUserPopupVisible] = useState(false);
  const [isIdCardPopupVisible, setIsIdCardPopupVisible] = useState(false);
  const [roleError, setRoleError] = useState('');
  const [rolesData, setRolesData] = useState([]);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    if (currentUserId) {
      axiosInstance.get(`/user/${currentUserId}/`)
        .then((response) => {
          setUserData(response.data);
          return axiosInstance.get(`/user/${currentUserId}/office-branch/`);
        })
        .then((branchResponse) => setBranch(branchResponse.data))
        .catch((error) => console.error('Error fetching user data or office branch:', error));

      axiosInstance.get(`/attendance/user/${currentUserId}/`)
        .then((response) => {
          setAttendanceData(response.data);
          if (response.data.length > 0) {
            const latestAttendance = response.data[response.data.length - 1];
            if (latestAttendance.punch_out_time === null) {
              setPunchStatus(true);
            } else {
              setPunchStatus(false);
            }
          } else {
            setPunchStatus(false); 
          }
        })
        .catch((error) => console.error('Error fetching attendance data:', error));
    }
  }, [currentUserId]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axiosInstance.get(`/users/${currentUserId}/roles/`); 
        setRoles([response.data.active_role, response.data.assigned_role]);
        setActiveRole(response.data.active_role);
      } catch (err) {
        console.error('Failed to fetch roles:', err);
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRolesData([res.data.active_role, res.data.assigned_role]);
      })
      .catch(console.error);
  }, []);

  const handleSwitchRole = async () => {
    try {
      const response = await axiosInstance.post('/switch-role/', { role_id: roles[1].id });
      setActiveRole(roles[1]);
      navigate('/admin-manager-lead-dashboard');
    } catch (err) {
      if (err.response?.data.detail === "You do not have permission to switch roles.") {
        setError("You don't have another role to switch to.");
      } else {
        console.error('Failed to switch role:', err.response?.data.detail);
      }
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const fetchUserQueries = () => {
    axiosInstance.get(`/queries/user/${currentUserId}/`)
      .then((response) => {
        setUserQueries(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  };
  
  useEffect(() => {
    fetchUserQueries();
  }, [currentUserId]);

  const profilePhotoUrl = userData?.profile_photo?.startsWith('http://') 
    ? userData?.profile_photo.replace('http://', 'https://') 
    : userData?.profile_photo;

  const menuItems = [
    { icon: '&#127968;', label: 'Dashboard', path: '/user-dashboard' },
    { icon: '&#128100;', label: 'My Profile', path: `/user-profile/${userData?.id}` },
    { icon: '&#128197;', label: 'Attendance', path: `/leave-attendance/${userData?.id}` },
    { icon: '&#128203;', label: 'Leave Management', path: `/leave-management/${userData?.id}` },
    // { icon: '&#128197;', label: 'Calendar', path: '/calendar', condition: userData?.role_name === 'employee' },
    // { icon: '&#128178;', label: 'Payroll', path: 'https://hrms.teamhalt.com/', active: false },
    { icon: '&#128179;', label: 'ID Card', action: () => setIsIdCardPopupVisible(true) },
  ];

  const quickLinks = [
    { icon: '&#128100;', label: 'My Profile', img: '/profile.jpg', link: `/user-profile/${userData?.id}` },
    { icon: '&#128203;', label: 'Attendance', img: '/leave.jpg', link: `/leave-attendance/${userData?.id}` },
    { icon: '&#128203;', label: 'Leave', img: '/apply-leave.jpg', link: `/leave-management/${userData?.id}` },
    // { icon: '&#128197;', label: 'Calendar', img: '/calendar.jpg', link: '/calendar' },
    { icon: '&#128179;', label: 'ID Card', img: '/ID.jpeg', action: () => setIsIdCardPopupVisible(true) },
  ];

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={userData} 
        activeRole={userData?.role_name}
        error={roleError}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout} 
        menuItems={menuItems}
      />
      
      <main className="dashboard-main-content">
        <div className="user-dashboard-grid">
          {/* Profile Card */}
          <div className="user-profile-card">
            <div className="profile-cover"></div>
            <div className="profile-info">
              <div className="profile-avatar">
                {profilePhotoUrl ? (
                  <img src={profilePhotoUrl} alt="Profile" />
                ) : (
                  <div className="avatar-placeholder">&#128100;</div>
                )}
              </div>
              <h2>{userData?.first_name} {userData?.last_name}</h2>
              <p className="designation">{userData?.designation || 'Employee'}</p>
              <p className="employee-id">Employee ID: {userData?.employee_code || 'N/A'}</p>
              <Link to={`/punch-map/${currentUserId}/`} className="mark-attendance-btn">
                <span>&#128205;</span> Mark Attendance
              </Link>
            </div>
          </div>

          {/* Quick Links */}
          <div className="quick-links-section">
            <h3>Quick Links</h3>
            <div className="quick-links-grid">
              {quickLinks.map((item, index) => (
                <div key={index} className="quick-link-card" onClick={() => item.action ? item.action() : null}>
                  {item.link ? (
                    <Link to={item.link}>
                      <div className="quick-link-img">
                        <img src={item.img} alt={item.label} />
                      </div>
                      <span>{item.label}</span>
                    </Link>
                  ) : (
                    <div className="quick-link-img">
                      <img src={item.img} alt={item.label} />
                      <span>{item.label}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Floating Query Button */}
        {/* <button
          className="floating-query-btn"
          onClick={() => setIsUserPopupVisible(!isUserPopupVisible)}
        >
          <span>&#9993;</span>
        </button>
      
        {isUserPopupVisible && (
          <div className="query-popup">
            <div className="query-popup-header">
              <h4>My Queries</h4>
              <button onClick={() => setIsUserPopupVisible(false)}>&times;</button>
            </div>
            <div className="query-popup-content">
              {userQueries.length > 0 ? (
                userQueries.map((query, index) => (
                  <div key={index} className="query-item">
                    <p><strong>Query:</strong> {query.query}</p>
                    <p><strong>User:</strong> {query.user_name} ({query.employee_code})</p>
                    <p><strong>Status:</strong> <span className={`status-${query.status?.toLowerCase()}`}>{query.status}</span></p>
                  </div>
                ))
              ) : (
                <p className="no-queries">No queries found</p>
              )}
            </div>
          </div>
        )} */}
      </main>

      {/* ID Card Popup */}
      {isIdCardPopupVisible && (
        <div className="id-card-overlay" onClick={() => setIsIdCardPopupVisible(false)}>
          <div className="id-card-popup" onClick={(e) => e.stopPropagation()}>
            <button className="id-card-close" onClick={() => setIsIdCardPopupVisible(false)}>&times;</button>
            
            <div className="id-card-header">
              <img src="./Teamhalt.png" alt="Logo" className="id-card-logo" />
              <span>TEAMHALT PRIVATE LIMITED</span>
            </div>
            
            <div className="id-card-body">
              <div className="id-card-profile">
                <img
                  src={profilePhotoUrl || '/profile.jpg'}
                  alt="Profile"
                  className="id-card-photo"
                />
                <div className="id-card-info">
                  <h4>{userData?.first_name} {userData?.last_name}</h4>
                  <p className="id-designation">{userData?.designation}</p>
                  <div className="id-dob">
                    <strong>DOB:</strong>
                    <span>
                      {userData?.date_of_birth
                        ? new Date(userData.date_of_birth).toLocaleDateString("en-GB").replace(/\//g, "-")
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
              
              <hr className="id-card-divider" />
              
              <div className="id-card-details">
                {[
                  { label: "Emp ID", value: userData?.employee_code },
                  { label: "Department", value: userData?.department },
                  { label: "ESIC No.", value: userData?.esic_no },
                  { label: "UAN No.", value: userData?.uan_no },
                  { label: "Gender", value: userData?.gender },
                  { label: "Contact No.", value: userData?.contact_number },
                ].map((item, idx) => (
                  <div key={idx} className="detail-row">
                    <strong>{item.label}:</strong>
                    <span>{item.value || "N/A"}</span>
                  </div>
                ))}
              </div>
              
              {typeof userData?.work_location_name === "string" &&
                userData.work_location_name.toLowerCase() !== "noida" && (
                <div className="id-card-duty">
                  <strong>On Contractual Duty Of:</strong>
                  <span>{userData?.duty_of || "N/A"}</span>
                </div>
              )}
              
              <div className="id-card-footer">
                https://teamhalt.com/
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;


